import { useMemo } from 'react';
import { useSearchProducts, type Variant } from '../../../products';
import { type SkuStockLevels, useBatchFetchStock } from '../../../stock';

// `*` browses the whole synced catalogue rather than matching a search term.
const CATALOGUE_BROWSE_QUERY = '*';

// Sellable when stock is untracked, or any location can fulfil down to its
// oversell floor (`stock_level > min_stock_level`, e.g. -9 allows 9 past zero).
function isSellable(variant: Variant, stockBySku: Map<string, SkuStockLevels>): boolean {
  if (variant.stock_management === 'none') {
    return true;
  }
  const levels = stockBySku.get(variant.identifiers.sku)?.stock_levels ?? [];
  return levels.some((level) => level.stock_level > level.min_stock_level);
}

/**
 * SKU of the first live, sellable variant in the catalogue. `undefined` while the catalogue/stock
 * lookups are in flight; `null` once resolved with nothing sellable.
 */
export function useSellableSku(): string | null | undefined {
  const products = useSearchProducts(CATALOGUE_BROWSE_QUERY);

  const liveVariants = useMemo<Variant[]>(
    () =>
      (products.data ?? [])
        .flatMap((product) => product.variants)
        .filter((variant) => variant.status === 'live' && variant.identifiers.sku),
    [products.data],
  );

  // Only tracked variants need a stock lookup — skip the call when there are none.
  const trackedSkus = useMemo(
    () =>
      liveVariants
        .filter((variant) => variant.stock_management !== 'none')
        .map((variant) => variant.identifiers.sku),
    [liveVariants],
  );
  const stock = useBatchFetchStock(trackedSkus.length ? trackedSkus : undefined);

  const isPending = products.isPending || (trackedSkus.length > 0 && stock.isPending);

  return useMemo<string | null | undefined>(() => {
    if (isPending) {
      return undefined;
    }
    const stockBySku = new Map((stock.data ?? []).map((entry) => [entry.sku, entry]));
    const sellable = liveVariants.find((variant) => isSellable(variant, stockBySku));
    return sellable ? sellable.identifiers.sku : null;
  }, [isPending, liveVariants, stock.data]);
}
